import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TERMS_TEXT, PRIVACY_POLICY_TEXT } from './legalDocuments';

/* ────────────────────────────────────────
   New App Screenshots
   ──────────────────────────────────────── */
import ssHomePicks       from './assets/ss_home_picks_donovan_mitchell.png';
import ssChatLamelo      from './assets/ss_chat_lamelo_analysis.png';
import ssParlayResult    from './assets/ss_tools_parlay_evaluator_result.png';
import ssTrendsBrunson   from './assets/ss_trends_player_detail_brunson.png';
import ssResearch        from './assets/ss_research_player_splits_team_stats.png';
import ssChatTrailblazers from './assets/ss_chat_ai_response_trail_blazers.png';
import ssParlayInput     from './assets/ss_tools_parlay_evaluator_input.png';
import ssTrendsOverview  from './assets/ss_trends_overview_matchups.png';
import ssChatPipeline    from './assets/ss_chat_ai_analysis_pipeline.png';
import ssChatMatchup     from './assets/ss_chat_matchup_selector.png';
import logoImg           from './assets/propOracle_v2.png';

const APP_STORE_URL      = 'https://apps.apple.com/us/app/proporacle/id6755162723';
const SUPPORT_FORM_URL   = 'https://docs.google.com/forms/d/e/1FAIpQLSdq42_RUI2IuQZ5xLgt4tCULSW66zbmS-sr-cVu9PMP2EQnVg/viewform?embedded=true';
const TWITTER_URL        = 'https://x.com/PropOracle1';
const INSTAGRAM_URL      = 'https://instagram.com/proporacle';

const CAROUSEL_IMAGES = [
  ssHomePicks, ssChatLamelo, ssParlayResult, ssTrendsBrunson,
  ssResearch, ssChatTrailblazers, ssParlayInput,
  ssTrendsOverview, ssChatPipeline, ssChatMatchup,
];

/* ────────────────────────────────────────
   SVG Icons
   ──────────────────────────────────────── */
const TwitterIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InstagramIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const AppleIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

/* ────────────────────────────────────────
   Hooks
   ──────────────────────────────────────── */
function useOnScreen(options) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15, ...options }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, []); // options intentionally omitted — caller doesn't change it
  return [ref, visible];
}

function useCountUp(target, duration = 1800) {
  const ref = useRef(null);
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(false);
  const started = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (!started.current) {
            started.current = true;
            const t0 = Date.now();
            const tick = () => {
              const p = Math.min((Date.now() - t0) / duration, 1);
              const e = 1 - Math.pow(1 - p, 3);
              setCount(Math.round(e * target));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
          }
        }
      },
      { threshold: 0.3 }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [target, duration]);
  return [ref, count, visible];
}

function useCarousel(length, interval = 3200) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const restart = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIndex(i => (i + 1) % length), interval);
  }, [length, interval]);
  useEffect(() => { restart(); return () => clearInterval(timerRef.current); }, [restart]);
  const jumpTo = useCallback((i) => { setIndex(i); restart(); }, [restart]);
  return [index, jumpTo];
}

function useMouseParallax() {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => setPos({
      x: (e.clientX / window.innerWidth - 0.5) * 2,
      y: (e.clientY / window.innerHeight - 0.5) * 2,
    });
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);
  return pos;
}

function useTypewriterCycle(phrases, interval = 3500) {
  const [idx, setIdx] = useState(0);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const id = setInterval(() => {
      setFading(true);
      setTimeout(() => { setIdx(i => (i + 1) % phrases.length); setFading(false); }, 420);
    }, interval);
    return () => clearInterval(id);
  }, [phrases.length, interval]);
  return [phrases[idx], fading];
}

function useHashRoute() {
  const getHash = () => window.location.hash.replace(/^#\/?/, '') || 'home';
  const [route, setRoute] = useState(getHash);
  useEffect(() => {
    const fn = () => setRoute(getHash());
    window.addEventListener('hashchange', fn);
    return () => window.removeEventListener('hashchange', fn);
  }, []);
  const navigate = useCallback((path) => {
    window.location.hash = path === 'home' ? '' : `/${path}`;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  return [route, navigate];
}

/* ────────────────────────────────────────
   Reusable animated wrapper
   ──────────────────────────────────────── */
const FadeIn = ({ children, direction = 'up', delay = 0, className = '' }) => {
  const [ref, visible] = useOnScreen();
  const cls = direction === 'left' ? 'fade-in-left' : direction === 'right' ? 'fade-in-right' : 'fade-in';
  return (
    <div ref={ref} className={`${cls} ${visible ? 'visible' : ''} ${className}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
};

/* ────────────────────────────────────────
   iPhone Mockup
   ──────────────────────────────────────── */
const IPhoneMockup = ({ imageSrc, floating = true, style = {}, className = '' }) => (
  <div className={`iphone-frame ${floating ? 'float-slow' : ''} ${className}`} style={style}>
    <div className="iphone-device">
      <div className="iphone-notch" />
      <div className="iphone-screen">
        {imageSrc && <img src={imageSrc} alt="PropOracle App Screen" />}
      </div>
    </div>
  </div>
);

/* ────────────────────────────────────────
   Navbar
   ──────────────────────────────────────── */
const Navbar = ({ navigate }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const close = () => setMenuOpen(false);

  return (
    <nav className="navbar">
      <div className="navbar-brand" onClick={() => { navigate('home'); close(); }} style={{ cursor: 'pointer' }}>
        <img src={logoImg} alt="PropOracle" className="navbar-logo-img" />
        <h1>PropOracle</h1>
      </div>

      <button className="nav-toggle" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
        <span className={`hamburger ${menuOpen ? 'open' : ''}`} />
      </button>

      <div className={`navbar-links ${menuOpen ? 'open' : ''}`}>
        <a href="#about" onClick={close}>About</a>
        <a href="#features" onClick={close}>Features</a>
        <a href="#/terms" onClick={close}>Terms</a>
        <a href="#/privacy" onClick={close}>Privacy</a>
        <button className="nav-text-btn" onClick={() => { navigate('contact'); close(); }}>Contact</button>
        <button className="nav-text-btn" onClick={() => { navigate('support'); close(); }}>Support</button>
        <div className="navbar-social">
          <a href={TWITTER_URL} target="_blank" rel="noopener noreferrer" className="social-icon-link" aria-label="Twitter/X">
            <TwitterIcon size={17} />
          </a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="social-icon-link" aria-label="Instagram">
            <InstagramIcon size={17} />
          </a>
        </div>
      </div>
    </nav>
  );
};

/* ────────────────────────────────────────
   Hero
   ──────────────────────────────────────── */
const Hero = () => {
  const mouse = useMouseParallax();
  const phrases = [
    'Build data-driven parlays.',
    'Analyze player props in real time.',
    'Track every bet with AI.',
    'Find the edge before tip-off.',
  ];
  const [phrase, fading] = useTypewriterCycle(phrases, 3500);

  const phoneStyle = {
    transform: `perspective(1200px) rotateY(${mouse.x * 7}deg) rotateX(${mouse.y * -3.5}deg)`,
    transition: 'transform 0.15s ease-out',
  };

  return (
    <section className="hero">
      <div className="hero-orb orb-1" />
      <div className="hero-orb orb-2" />
      <div className="hero-orb orb-3" />

      <div className="hero-content">
        <div className="hero-text">
          <div className="hero-badge">
            <span className="badge-dot badge-dot-live" />
            Now Live on the App Store
          </div>
          <h2 className="hero-title">
            Smarter Bets,<br />
            <span className="gradient-text">Powered by AI</span>
          </h2>
          <p className="hero-subtitle">
            <span className={`typewriter-text ${fading ? 'fading' : ''}`}>{phrase}</span>
          </p>
          <div className="hero-actions">
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn-appstore">
              <AppleIcon size={18} /> Download on App Store
            </a>
            <a href="#features" className="btn-secondary">
              See Features
            </a>
          </div>
          <div className="hero-trust">
            <span className="trust-item"><span className="trust-dot" />NBA data updated daily</span>
            <span className="trust-item"><span className="trust-dot" />Free to download on iOS</span>
          </div>
        </div>

        <FadeIn direction="right" delay={0.2}>
          <IPhoneMockup imageSrc={ssHomePicks} style={phoneStyle} />
        </FadeIn>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────
   Stats Banner
   ──────────────────────────────────────── */
const StatItem = ({ target, display, label, delay }) => {
  const [ref, count, visible] = useCountUp(target);
  return (
    <div ref={ref} className={`stat-item fade-in ${visible ? 'visible' : ''}`} style={{ transitionDelay: `${delay}s` }}>
      <div className="stat-number">{display(count)}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

const StatsBanner = () => {
  const stats = [
    { target: 498, display: n => `${n}+`,   label: 'Bets Tracked & Verified', delay: 0 },
    { target: 565, display: n => `${(n / 10).toFixed(1)}%`, label: 'Verified Pick Accuracy', delay: 0.1 },
    { target: 116, display: n => `${n}+`,   label: 'Playoff Picks Analyzed',  delay: 0.2 },
    { target: 388, display: n => `${n}+`,   label: 'Matchups Analyzed',        delay: 0.3 },
  ];

  return (
    <div className="stats-banner">
      <div className="stats-banner-inner">
        {stats.map((s, i) => <StatItem key={i} {...s} />)}
      </div>
    </div>
  );
};

/* ────────────────────────────────────────
   Screenshot Carousel
   ──────────────────────────────────────── */
const ScreenshotCarousel = () => {
  const [activeIndex, jumpTo] = useCarousel(CAROUSEL_IMAGES.length, 3200);
  const len = CAROUSEL_IMAGES.length;
  const prev = (activeIndex - 1 + len) % len;
  const next = (activeIndex + 1) % len;

  return (
    <section className="carousel-section">
      <div className="section-container" style={{ textAlign: 'center' }}>
        <FadeIn>
          <div className="section-label">✦ App Screens</div>
          <h2 className="section-title">See It In Action</h2>
          <p className="section-subtitle" style={{ margin: '0 auto 56px' }}>
            Every screen built for speed and clarity — from AI chat to live odds.
          </p>
        </FadeIn>

        <div className="carousel-stage">
          <div className="phone-side phone-left">
            <IPhoneMockup imageSrc={CAROUSEL_IMAGES[prev]} floating={false} />
          </div>
          <div className="phone-center">
            <IPhoneMockup imageSrc={CAROUSEL_IMAGES[activeIndex]} floating={false} />
          </div>
          <div className="phone-side phone-right">
            <IPhoneMockup imageSrc={CAROUSEL_IMAGES[next]} floating={false} />
          </div>
        </div>

        <div className="carousel-dots">
          {CAROUSEL_IMAGES.map((_, i) => (
            <button
              key={i}
              className={`carousel-dot ${i === activeIndex ? 'active' : ''}`}
              onClick={() => jumpTo(i)}
              aria-label={`Screenshot ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────
   Features Grid
   ──────────────────────────────────────── */
const FeaturesGrid = () => {
  const features = [
    { icon: '💬', title: 'AI Chat Assistant',    desc: 'Have a natural conversation with our AI to build parlays, get instant bet analysis, and ask about any player matchup.' },
    { icon: '📊', title: 'Advanced Analytics',   desc: 'Dive into hit rates, rolling averages, home/away splits, and opponent-adjusted projections for every active player.' },
    { icon: '⚡', title: 'Real-Time Signals',    desc: 'Instant alerts when our models detect high-value props based on today\'s lines and recent performance trends.' },
    { icon: '🎯', title: 'Parlay Builder',        desc: 'Build and optimize multi-leg parlays with AI-calculated confidence scores and correlation risk assessments.' },
    { icon: '📈', title: 'Bet Tracking',          desc: 'Log every bet, monitor your P&L, and visualize performance trends with detailed dashboards over any timeframe.' },
    { icon: '🔍', title: 'Outcome Verification', desc: 'Auto-verify your bets against real box scores so you always know exactly where you stand — no manual lookup needed.' },
  ];

  return (
    <section id="features" className="parallax-section">
      <div className="bg-gradient-overlay" />
      <div className="section-container" style={{ textAlign: 'center' }}>
        <FadeIn>
          <div className="section-label">✦ Core Features</div>
          <h2 className="section-title" style={{ maxWidth: 600, margin: '0 auto 16px' }}>
            Everything You Need to Bet Smarter
          </h2>
          <p className="section-subtitle" style={{ margin: '0 auto 52px' }}>
            From AI-driven analysis to real-time tracking, PropOracle puts professional-grade tools in your pocket.
          </p>
        </FadeIn>

        <div className="features-grid">
          {features.map((f, i) => (
            <FadeIn key={i} delay={i * 0.07}>
              <div className="feature-card">
                <div className="feature-card-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────
   Feature Showcase — 3 sections
   ──────────────────────────────────────── */
const FeatureShowcase = () => (
  <section id="how-it-works" className="parallax-section">
    <div className="section-container">

      {/* 1 — AI Chat */}
      <div className="feature-showcase" style={{ marginBottom: 120 }}>
        <FadeIn direction="left">
          <div className="feature-info">
            <div className="section-label">💬 AI Assistant</div>
            <h2 className="section-title">Chat Your Way<br />to Better Bets</h2>
            <p className="section-subtitle">
              Just ask — our AI understands natural language and gives you instant, data-backed answers about any player, game, or prop.
            </p>
            <ul className="feature-list">
              <li className="feature-list-item">
                <div className="feature-icon">🗣️</div>
                <div>
                  <h4>Natural Language Queries</h4>
                  <p>"What are LaMelo's 3-point stats in away games this month?"</p>
                </div>
              </li>
              <li className="feature-list-item">
                <div className="feature-icon">🧠</div>
                <div>
                  <h4>Context-Aware Responses</h4>
                  <p>The AI remembers your conversation and builds on previous analysis.</p>
                </div>
              </li>
              <li className="feature-list-item">
                <div className="feature-icon">🎲</div>
                <div>
                  <h4>Instant Parlay Builder</h4>
                  <p>"Build me a 3-leg parlay for tonight's games" — done in seconds.</p>
                </div>
              </li>
            </ul>
          </div>
        </FadeIn>
        <FadeIn direction="right" delay={0.15}>
          <IPhoneMockup imageSrc={ssChatLamelo} />
        </FadeIn>
      </div>

      {/* 2 — Parlay Builder */}
      <div className="feature-showcase reverse" style={{ marginBottom: 120 }}>
        <FadeIn direction="right">
          <div className="feature-info">
            <div className="section-label">🎯 Parlay Builder</div>
            <h2 className="section-title">Build Parlays<br />With Confidence</h2>
            <p className="section-subtitle">
              Our AI evaluates every leg of your parlay against historical data and live odds — so you know which combos have real edge.
            </p>
            <ul className="feature-list">
              <li className="feature-list-item">
                <div className="feature-icon">✅</div>
                <div>
                  <h4>AI Confidence Scores</h4>
                  <p>Each leg is scored 0–100% based on historical hit rate and current matchup.</p>
                </div>
              </li>
              <li className="feature-list-item">
                <div className="feature-icon">📉</div>
                <div>
                  <h4>Live Odds Integration</h4>
                  <p>Pulls live lines from major sportsbooks to evaluate true expected value on each leg.</p>
                </div>
              </li>
              <li className="feature-list-item">
                <div className="feature-icon">🏆</div>
                <div>
                  <h4>Multi-Leg Optimization</h4>
                  <p>Identify the strongest 2- to 5-leg combinations across all of tonight's games.</p>
                </div>
              </li>
            </ul>
          </div>
        </FadeIn>
        <FadeIn direction="left" delay={0.15}>
          <IPhoneMockup imageSrc={ssParlayResult} />
        </FadeIn>
      </div>

      {/* 3 — Trends & Analytics */}
      <div className="feature-showcase">
        <FadeIn direction="left">
          <div className="feature-info">
            <div className="section-label">📈 Player Trends</div>
            <h2 className="section-title">Deep Trends,<br />Sharper Edges</h2>
            <p className="section-subtitle">
              Go beyond basic stats. Explore rolling performance windows, opponent-adjusted splits, and injury-aware projections.
            </p>
            <ul className="feature-list">
              <li className="feature-list-item">
                <div className="feature-icon">📊</div>
                <div>
                  <h4>Rolling Hit Rates</h4>
                  <p>L5, L10, L20 hit rates for any player prop — filtered by opponent, home/away, and more.</p>
                </div>
              </li>
              <li className="feature-list-item">
                <div className="feature-icon">🏀</div>
                <div>
                  <h4>Matchup Intelligence</h4>
                  <p>See how each opponent defends the specific stat you're targeting.</p>
                </div>
              </li>
              <li className="feature-list-item">
                <div className="feature-icon">🔬</div>
                <div>
                  <h4>Research Mode</h4>
                  <p>Full team stats, player splits, pace adjustments, and rest-day analysis in one view.</p>
                </div>
              </li>
            </ul>
          </div>
        </FadeIn>
        <FadeIn direction="right" delay={0.15}>
          <IPhoneMockup imageSrc={ssTrendsBrunson} />
        </FadeIn>
      </div>

    </div>
  </section>
);

/* ────────────────────────────────────────
   About Section
   ──────────────────────────────────────── */
const AboutSection = () => (
  <section id="about" className="parallax-section about-section">
    <div className="section-container">
      <FadeIn>
        <div className="section-label" style={{ justifyContent: 'center' }}>✦ Our Mission</div>
        <h2 className="section-title" style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 16px' }}>
          Built for the Modern Sports Fan
        </h2>
        <p className="section-subtitle" style={{ textAlign: 'center', margin: '0 auto 56px' }}>
          We believe data-driven betting should be accessible to everyone — not just quants with spreadsheets.
        </p>
      </FadeIn>

      <div className="about-grid">
        {[
          { icon: '🏀', title: 'Why We Built This', desc: 'Prop betting is drowning in noise. We built PropOracle to cut through it — using real NBA data, machine learning models, and an AI assistant you can actually have a conversation with.' },
          { icon: '🤖', title: 'How It Works',      desc: 'Our backend analyzes thousands of props daily — factoring in player trends, opponent splits, injury reports, and live odds — then distills it into clear signals and confidence scores.' },
          { icon: '📱', title: 'Mobile-First',      desc: 'Designed from the ground up for iPhone. Fast, clean, and built to give you the edge on any game night — right from your pocket.' },
          { icon: '🔒', title: 'Responsible Use',   desc: 'PropOracle is an analytics tool, not a betting service. We provide data-driven insights to inform your decisions with full transparency on every metric we show.' },
        ].map((card, i) => (
          <FadeIn key={i} direction={i % 2 === 0 ? 'left' : 'right'} delay={i * 0.1}>
            <div className="about-card">
              <div className="about-card-icon">{card.icon}</div>
              <h3>{card.title}</h3>
              <p>{card.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

/* ────────────────────────────────────────
   CTA Section
   ──────────────────────────────────────── */
const CTASection = () => (
  <section id="get-started" className="cta-section">
    <div className="cta-dot-grid" />
    <div className="cta-glow" />
    <FadeIn>
      <div className="section-label" style={{ justifyContent: 'center' }}>🏀 Available Now</div>
      <h2 className="cta-title">Ready to Bet Smarter?</h2>
      <p className="cta-subtitle">
        PropOracle is live on the App Store. Download free and start making data-backed picks tonight.
      </p>
      <div className="cta-actions">
        <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="btn-appstore cta-appstore-btn">
          <AppleIcon size={20} /> Download on App Store
        </a>
      </div>
      <p className="cta-disclaimer">Free to download. iOS only. Must be 21+ to wager.</p>
    </FadeIn>
  </section>
);

/* ────────────────────────────────────────
   Footer
   ──────────────────────────────────────── */
const Footer = ({ navigate }) => (
  <footer className="footer">
    <div className="footer-content">
      <div className="footer-brand-col">
        <div className="footer-brand">
          <img src={logoImg} alt="PropOracle" className="footer-logo-img" />
          <span>PropOracle</span>
        </div>
        <p className="footer-tagline">AI-powered NBA prop analytics for the modern bettor.</p>
        <div className="footer-social">
          <a href={TWITTER_URL} target="_blank" rel="noopener noreferrer" className="social-icon-link" aria-label="Twitter/X">
            <TwitterIcon size={18} />
          </a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="social-icon-link" aria-label="Instagram">
            <InstagramIcon size={18} />
          </a>
        </div>
      </div>

      <div className="footer-cols">
        <div className="footer-col">
          <div className="footer-col-title">Product</div>
          <a href="#features">Features</a>
          <a href="#how-it-works">How It Works</a>
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">Download on App Store</a>
        </div>
        <div className="footer-col">
          <div className="footer-col-title">Legal</div>
          <a href="#/terms">Terms of Service</a>
          <a href="#/privacy">Privacy Policy</a>
        </div>
        <div className="footer-col">
          <div className="footer-col-title">Connect</div>
          <button className="footer-nav-btn" onClick={() => navigate('contact')}>Contact Us</button>
          <button className="footer-nav-btn" onClick={() => navigate('support')}>Support</button>
          <a href={TWITTER_URL} target="_blank" rel="noopener noreferrer">Twitter / X</a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">Instagram</a>
        </div>
      </div>
    </div>

    <div className="footer-bottom">
      <div className="footer-copy">© 2026 PropOracle. All rights reserved.</div>
      <div className="footer-disclaimer">For entertainment purposes only. Must be 21+ to wager.</div>
    </div>
  </footer>
);

/* ────────────────────────────────────────
   Legal Page
   ──────────────────────────────────────── */
const LegalPage = ({ section, navigate }) => {
  useEffect(() => {
    if (section === 'terms' || section === 'privacy') {
      const el = document.getElementById(section);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [section]);

  return (
    <main className="legal-page">
      <div className="legal-page-inner">
        <button className="legal-back-btn" onClick={() => navigate('home')}>← Back to Home</button>
        <div className="legal-toc">
          <a href="#/terms" className="legal-toc-link">Terms of Service</a>
          <span className="legal-toc-sep">·</span>
          <a href="#/privacy" className="legal-toc-link">Privacy Policy</a>
        </div>
        <section id="terms" className="legal-section">
          <h1 className="legal-title">Terms of Service</h1>
          <pre className="legal-text">{TERMS_TEXT}</pre>
        </section>
        <div className="section-divider" />
        <section id="privacy" className="legal-section">
          <h1 className="legal-title">Privacy Policy</h1>
          <pre className="legal-text">{PRIVACY_POLICY_TEXT}</pre>
        </section>
      </div>
    </main>
  );
};

/* ────────────────────────────────────────
   Support Page (embedded Google Form)
   ──────────────────────────────────────── */
const SupportPage = ({ navigate }) => (
  <main className="support-page">
    <div className="support-page-inner">
      <button className="legal-back-btn" onClick={() => navigate('home')}>← Back to Home</button>
      <div className="support-header">
        <div className="section-label">🛠️ Support</div>
        <h1 className="support-title">How Can We Help?</h1>
        <p className="support-subtitle">
          Fill out the form below and our team will get back to you within 1–2 business days.
          For urgent issues, email us directly at{' '}
          <a href="mailto:admin@proporacleai.com" style={{ color: 'var(--accent)' }}>admin@proporacleai.com</a>.
        </p>
      </div>

      <div className="support-form-wrap">
        <iframe
          src={SUPPORT_FORM_URL}
          className="support-form-iframe"
          title="PropOracle Support Form"
          allowFullScreen
          sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
        />
        <div className="support-form-note">
          Having trouble with the form?{' '}
          <a href="mailto:admin@proporacleai.com">Email us directly</a> instead.
        </div>
      </div>
    </div>
  </main>
);

/* ────────────────────────────────────────
   Contact Page
   ──────────────────────────────────────── */
const ContactPage = ({ navigate }) => (
  <main className="contact-page">
    <div className="contact-page-inner">
      <button className="legal-back-btn" onClick={() => navigate('home')}>← Back to Home</button>
      <div className="contact-card">
        <div className="contact-icon">✉️</div>
        <h1 className="contact-title">Contact Us</h1>
        <p className="contact-subtitle">
          Have a question, feedback, or partnership inquiry? We'd love to hear from you.
        </p>
        <a href="mailto:admin@proporacleai.com" className="contact-email-btn">
          admin@proporacleai.com
        </a>
        <p className="contact-note">
          For app support, feature requests, or business inquiries.<br />We typically respond within 1–2 business days.
        </p>
        <div className="contact-divider" />
        <p className="contact-follow-label">Follow us</p>
        <div className="contact-social-links">
          <a href={TWITTER_URL} target="_blank" rel="noopener noreferrer" className="social-btn">
            <TwitterIcon size={17} /> Twitter / X
          </a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="social-btn">
            <InstagramIcon size={17} /> Instagram
          </a>
        </div>
      </div>
    </div>
  </main>
);

/* ────────────────────────────────────────
   App Root
   ──────────────────────────────────────── */
export default function App() {
  const [route, navigate] = useHashRoute();
  const isLegal   = route === 'terms' || route === 'privacy';
  const isContact = route === 'contact';
  const isSupport = route === 'support';

  return (
    <>
      <Navbar navigate={navigate} />
      {isLegal ? (
        <LegalPage section={route} navigate={navigate} />
      ) : isContact ? (
        <ContactPage navigate={navigate} />
      ) : isSupport ? (
        <SupportPage navigate={navigate} />
      ) : (
        <>
          <Hero />
          <div className="section-divider" />
          <StatsBanner />
          <div className="section-divider" />
          <ScreenshotCarousel />
          <div className="section-divider" />
          <FeaturesGrid />
          <div className="section-divider" />
          <FeatureShowcase />
          <div className="section-divider" />
          <AboutSection />
          <div className="section-divider" />
          <CTASection />
        </>
      )}
      <Footer navigate={navigate} />
    </>
  );
}
